import React from "react";

import { Col, FormGroup, Input } from "reactstrap";

/**
 * One name/link row of an Array:links editor, shared by the create wizard
 * (TemplateSelector/ArrayLinks) and the update screens
 * (UpdatePage/ContentArrayLinks) — SCRUM-2166, slice 2 of SCRUM-2122.
 *
 * The two screens differ in one pinned way, kept here as a prop: the update
 * screen's inputs are controlled (`value` from the row), the wizard's are not.
 */

/** A row as both editors keep it; `index` is the editor's own 1-based key. */
export type LinkRowLike = { index: number; name?: string; link?: string };

/**
 * Writes an input's edit onto its row and returns the list to report.
 *
 * Kept exactly as both copies behaved: the row object is updated in place and
 * the same `links` array is returned, so the host receives the editor's own
 * list (the update screen's controlled inputs show the new text once the host
 * re-renders).
 */
export const handleLink = <R extends LinkRowLike>(
  links: R[],
  e: React.ChangeEvent<HTMLInputElement>,
  link: R
): R[] => {
  if (e.target.name === "name") {
    link.name = e.target.value;
  } else {
    link.link = e.target.value;
  }
  return links;
};

export type LinkRowFieldsProps<R extends LinkRowLike> = {
  link: R;
  /** true: the inputs carry `value` from the row (update screen). */
  controlled?: boolean;
  onEdit: (e: React.ChangeEvent<HTMLInputElement>, link: R) => void;
};

export function LinkRowFields<R extends LinkRowLike>({ link, controlled = false, onEdit }: LinkRowFieldsProps<R>) {
  const nameValue = controlled ? { value: link.name } : {};
  const linkValue = controlled ? { value: link.link } : {};
  return (
    <FormGroup className="row">
      <Col md="2">
        <label className="form-control-label" style={{ color: "#597ade" }}>Name</label>
      </Col>
      <Col md="4">
        <Input
          {...nameValue}
          className="form-control-sm"
          placeholder="name"
          type="text"
          name="name"
          onChange={(e) => onEdit(e, link)}
        />
      </Col>
      <Col md="2">
        <label className="form-control-label" style={{ color: "#597ade" }}>Link</label>
      </Col>
      <Col md="4">
        <Input
          {...linkValue}
          className="form-control-sm"
          placeholder="link"
          name="link"
          type="text"
          onChange={(e) => onEdit(e, link)}
        />
      </Col>
    </FormGroup>
  );
}
